import { useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

// A voice note in a chat bubble. Its length shows before you press play (the
// sender measures it and sends it along, because browsers often cannot read the
// length of a fresh recording), and counts up from 0:00 once it plays.

const fmt = (s: number) => {
  const t = Math.max(0, Math.round(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

export default function VoiceNote({ src, duration, mine }: { src?: string; duration?: number; mine: boolean }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(0);
  const [found, setFound] = useState(0); // the length the browser could read, if it could
  const total = found || duration || 0;

  const onMeta = () => {
    const a = ref.current;
    if (!a) return;
    if (Number.isFinite(a.duration) && a.duration > 0) { setFound(a.duration); return; }
    // A WebM recording can report "unknown" length. Seeking far past the end makes the
    // browser work it out; then it goes back to the start. Only needed when the sender sent no length.
    if (a.duration === Infinity && !duration) {
      const back = () => {
        a.removeEventListener('timeupdate', back);
        a.currentTime = 0;
        if (Number.isFinite(a.duration)) setFound(a.duration);
      };
      a.addEventListener('timeupdate', back);
      a.currentTime = 1e101;
    }
  };
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setPlaying(false)); else a.pause();
  };
  const seek = (v: number) => {
    const a = ref.current;
    if (!a || !total) return;
    a.currentTime = Math.min(v, total);
    setNow(a.currentTime);
  };

  return (
    <div className="flex items-center gap-2.5 py-0.5 w-[min(15.5rem,62vw)]">
      <audio ref={ref} src={src} preload="metadata" onLoadedMetadata={onMeta}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={() => { if (playing) setNow(ref.current?.currentTime ?? 0); }}
        onEnded={() => { setPlaying(false); setNow(0); if (ref.current) ref.current.currentTime = 0; }} />
      <button type="button" onClick={toggle}
        aria-label={playing ? 'Pause voice note' : `Play voice note${total ? `, ${fmt(total)}` : ''}`}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${mine ? 'bg-white/25 hover:bg-white/35' : 'bg-black/10 hover:bg-black/15'}`}>
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
      </button>
      <input type="range" min={0} max={total || 1} step={0.1} value={Math.min(now, total || 1)} disabled={!total}
        onChange={e => seek(Number(e.target.value))} aria-label="Voice note position"
        className="flex-1 min-w-0 h-1 cursor-pointer disabled:opacity-40" style={{ accentColor: 'currentColor' }} />
      <span className="shrink-0 text-xs tabular-nums opacity-80 min-w-[2.25rem] text-right" aria-hidden="true">
        {playing || now > 0 ? `${fmt(now)} / ${fmt(total)}` : total ? fmt(total) : '--:--'}
      </span>
    </div>
  );
}
